import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle2, Search, Trophy, Users, Video, MapPin, Zap, Sparkles } from 'lucide-react';

const provinces = [
  'Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Eastern Cape', 'Free State',
  'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape'
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#eaf7ef] via-[#fff8e6] to-white">
        {/* Decorative blobs */}
        <div className="mft-blob mft-blob--green" style={{ width: 320, height: 320, top: -80, left: -60 }} />
        <div className="mft-blob mft-blob--gold" style={{ width: 260, height: 260, top: 80, right: -60 }} />
        <div className="mft-blob mft-blob--coral" style={{ width: 220, height: 220, bottom: -40, left: '40%' }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="max-w-3xl mx-auto text-center mft-fade-in">
            <span className="inline-flex items-center gap-1.5 bg-white/70 backdrop-blur border border-[color:var(--mft-green)]/20 text-[color:var(--mft-green)] text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm">
              <Sparkles className="w-3.5 h-3.5 text-[color:var(--mft-gold)]" /> South Africa's free-agent football hub
            </span>

            <h1 className="mt-6 text-4xl md:text-6xl font-black text-gray-900 leading-[1.05] tracking-tight">
              Get discovered. <br />
              <span className="mft-gradient-text">Build your football career.</span>
            </h1>

            <p className="mt-5 text-lg text-gray-600 max-w-xl mx-auto leading-relaxed">
              Build a complete player profile with photos, videos, and stats — while teams and agents discover the next big name from all 9 provinces.
            </p>

            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/register">
                <Button className="mft-btn-primary h-12 px-7 text-base gap-2 rounded-full">
                  Create your profile <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="h-12 px-7 text-base rounded-full border-2">I already have an account</Button>
              </Link>
            </div>

            <div className="mt-8 flex items-center gap-2 text-sm text-gray-600 justify-center flex-wrap">
              <span className="inline-flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full border border-gray-200 shadow-sm">
                <CheckCircle2 className="w-4 h-4 text-[color:var(--mft-green)]" /> 100% free to join
              </span>
              <span className="inline-flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full border border-gray-200 shadow-sm">
                <Zap className="w-4 h-4 text-[color:var(--mft-gold)]" /> No agent needed
              </span>
              <span className="inline-flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full border border-gray-200 shadow-sm">
                <MapPin className="w-4 h-4 text-[color:var(--mft-coral)]" /> All 9 provinces
              </span>
            </div>

            {/* Stats bar */}
            <div className="mt-14 grid grid-cols-3 gap-4 max-w-2xl mx-auto">
              {[
                { n: '9', l: 'Provinces', c: 'text-[color:var(--mft-green)]' },
                { n: '11', l: 'Positions', c: 'text-[color:var(--mft-gold)]' },
                { n: '90s', l: 'Video clips', c: 'text-[color:var(--mft-coral)]' },
              ].map((s, i) => (
                <div key={i} className="bg-white/80 backdrop-blur border border-gray-200 rounded-2xl py-5 shadow-sm mft-card">
                  <div className={`text-3xl md:text-4xl font-black ${s.c}`}>{s.n}</div>
                  <div className="text-xs text-gray-500 mt-1 uppercase tracking-wider font-semibold">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Province ticker */}
        <div className="relative overflow-hidden py-4 border-y border-gray-200 bg-white/60 backdrop-blur">
          <div className="flex gap-8 mft-marquee-track whitespace-nowrap">
            {[...provinces, ...provinces].map((p, i) => (
              <span key={i} className="inline-flex items-center gap-2 text-sm text-gray-600 font-medium">
                <MapPin className="w-4 h-4 text-[color:var(--mft-green)]" /> {p}
                <span className="mx-3 text-[color:var(--mft-gold)]">•</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-block bg-[color:var(--mft-gold-soft)] text-amber-900 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">
              How it works
            </span>
            <h2 className="mt-3 text-3xl md:text-4xl font-black text-gray-900">Everything you need to get seen</h2>
            <p className="mt-3 text-gray-600">Show off your journey, your skill, and your availability — all in one place.</p>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[
              { icon: Users, title: 'Full career profile', text: 'Name, nickname, position, age, province and every team you have played for.', ring: 'from-emerald-200 to-emerald-50', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-700' },
              { icon: Video, title: 'Photos & video clips', text: 'Up to 6 match photos and 5 clips of up to 90 seconds showcasing your skill.', ring: 'from-amber-200 to-amber-50', iconBg: 'bg-amber-100', iconColor: 'text-amber-700' },
              { icon: Search, title: 'Smart search', text: 'Teams filter by name, position and province to find exactly who they need.', ring: 'from-rose-200 to-rose-50', iconBg: 'bg-rose-100', iconColor: 'text-rose-700' },
            ].map((f, i) => (
              <div key={i} className={`mft-card relative bg-gradient-to-br ${f.ring} border border-white rounded-2xl p-6 shadow-sm`}>
                <div className={`w-12 h-12 rounded-xl ${f.iconBg} flex items-center justify-center shadow-sm`}>
                  <f.icon className={`w-6 h-6 ${f.iconColor}`} />
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Step {i + 1}</span>
                </div>
                <h3 className="mt-1 text-lg font-black text-gray-900">{f.title}</h3>
                <p className="mt-2 text-sm text-gray-700 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Positions strip */}
      <section className="py-14 bg-[color:var(--mft-sky)] mft-stripe relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <span className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">
            <Trophy className="w-3.5 h-3.5 text-[color:var(--mft-gold)]" /> Every role covered
          </span>
          <h2 className="mt-3 text-3xl md:text-4xl font-black text-gray-900">From keeper to striker</h2>
          <p className="mt-2 text-gray-600 max-w-xl mx-auto">List your position and let scouts filter by exactly what they need.</p>

          <div className="mt-8 flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
            {[
              { name: 'Goalkeeper', cls: 'pos-badge-goalkeeper' },
              { name: 'Right Back', cls: 'pos-badge-defender' },
              { name: 'Left Back', cls: 'pos-badge-defender' },
              { name: 'Centre Back', cls: 'pos-badge-defender' },
              { name: 'Defensive Mid', cls: 'pos-badge-midfielder' },
              { name: 'Central Mid', cls: 'pos-badge-midfielder' },
              { name: 'Attacking Mid', cls: 'pos-badge-midfielder' },
              { name: 'Right Winger', cls: 'pos-badge-winger' },
              { name: 'Left Winger', cls: 'pos-badge-winger' },
              { name: 'Striker', cls: 'pos-badge-forward' },
              { name: 'Second Striker', cls: 'pos-badge-forward' },
            ].map((p) => (
              <span key={p.name} className={`text-sm font-semibold px-4 py-2 rounded-full ${p.cls} shadow-sm`}>
                {p.name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="mft-blob mft-blob--green" style={{ width: 300, height: 300, top: 20, left: -60, opacity: 0.35 }} />
        <div className="mft-blob mft-blob--gold" style={{ width: 260, height: 260, bottom: -40, right: -40, opacity: 0.35 }} />
        <div className="relative max-w-3xl mx-auto text-center px-4">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[color:var(--mft-green)] shadow-lg">
            <Trophy className="w-7 h-7 text-[color:var(--mft-gold)]" />
          </div>
          <h2 className="mt-5 text-3xl md:text-4xl font-black text-gray-900">Your career starts today</h2>
          <p className="mt-3 text-gray-600">Sign up in seconds, upload your profile, and get discovered by teams across South Africa.</p>
          <Link to="/register">
            <Button className="mft-btn-primary mt-7 h-12 px-8 text-base gap-2 rounded-full">
              Get started free <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="py-8 text-center text-sm text-gray-500 border-t border-gray-100 bg-white">
        <div className="inline-flex items-center gap-2">
          <span className="mft-dot" />
          © {new Date().getFullYear()} MzansiFootball Talent Connect. Made in South Africa.
          <span className="mft-dot" />
        </div>
      </footer>
    </div>
  );
}
